/**
 * Leaflet, loaded on demand from the CDN.
 *
 * Leaflet is a ~150KB payload used by three screens out of dozens, so it is not
 * a bundle dependency — the script and its stylesheet are injected the first
 * time a map is actually rendered, and every later caller gets the same
 * already-loaded copy.
 */

let loaded = false;
let loading = false;
const waiting: Array<() => void> = [];

export function loadLeaflet(onReady: () => void): void {
  if (loaded) {
    onReady();
    return;
  }
  waiting.push(onReady);
  if (loading) return;
  loading = true;

  const link = document.createElement("link");
  link.rel = "stylesheet";
  link.href = "https://unpkg.com/leaflet@1.9.4/dist/leaflet.css";
  document.head.appendChild(link);

  const script = document.createElement("script");
  script.src = "https://unpkg.com/leaflet@1.9.4/dist/leaflet.js";
  script.onload = () => {
    loaded = true;
    loading = false;
    waiting.splice(0).forEach((fn) => fn());
  };
  script.onerror = () => {
    // Leave `loaded` false so a later mount can retry; the callers all fall
    // back to a "map unavailable" state rather than hanging on a spinner.
    loading = false;
    waiting.splice(0);
  };
  document.head.appendChild(script);
}

/**
 * Decode a Google encoded polyline into [lat, lng] pairs.
 *
 * The server asks the Routes API for the road geometry of a whole journey and
 * hands back Google's encoded string rather than thousands of coordinate pairs
 * — a day's route is a few KB encoded and tens of KB expanded. This is the
 * standard algorithm from Google's polyline algorithm format spec.
 */
export function decodePolyline(encoded: string | null | undefined): Array<[number, number]> {
  if (!encoded) return [];
  const points: Array<[number, number]> = [];
  let index = 0;
  let lat = 0;
  let lng = 0;

  while (index < encoded.length) {
    let result = 0;
    let shift = 0;
    let byte: number;
    do {
      byte = encoded.charCodeAt(index++) - 63;
      result |= (byte & 0x1f) << shift;
      shift += 5;
    } while (byte >= 0x20);
    lat += result & 1 ? ~(result >> 1) : result >> 1;

    result = 0;
    shift = 0;
    do {
      byte = encoded.charCodeAt(index++) - 63;
      result |= (byte & 0x1f) << shift;
      shift += 5;
    } while (byte >= 0x20);
    lng += result & 1 ? ~(result >> 1) : result >> 1;

    points.push([lat / 1e5, lng / 1e5]);
  }
  return points;
}
